/**
 * diffViewModel.ts
 *
 * Pure functions that transform an AuditDiff into a typed DiffViewModel
 * ready for the re-audit UI page to consume.
 *
 * No DB calls. No email logic. No UI imports.
 */

import {
  AuditDiff,
  DiffViewModel,
  Recommendation,
  StoredAudit,
  AuditResult,
} from '../types';
import { recomputeAuditFromSnapshot, compareAuditResults } from './diffEngine';

// ─── Build Reaudit Payload ────────────────────────────────────────────────────

/**
 * Assembles the full AuditDiff from a stored audit by recomputing against
 * current pricing. This is the entry point for the diff page data load.
 */
export function buildReauditPayload(storedAudit: StoredAudit, userEmail?: string): AuditDiff {
  const newResult = recomputeAuditFromSnapshot(storedAudit);
  return compareAuditResults(storedAudit, newResult, userEmail);
}

// ─── Render Diff View Model ───────────────────────────────────────────────────

/**
 * Converts an AuditDiff into a DiffViewModel for the UI.
 *
 * Key transformation: splits recommendations into:
 * - changedRecommendations: old/new pairs where something differs
 * - unchangedRecommendations: recs identical in both old and new
 */
export function renderDiffViewModel(diff: AuditDiff): DiffViewModel {
  const { changedRecs, unchangedRecs } = classifyRecommendations(
    diff.oldRecommendations,
    diff.newRecommendations,
  );

  const deltaDirection =
    diff.deltaMonthlySavings > 1
      ? 'improved'
      : diff.deltaMonthlySavings < -1
      ? 'worsened'
      : 'unchanged';

  return {
    auditId: diff.auditId,
    publicId: diff.publicId,
    createdAt: diff.createdAt,
    oldMonthlySavings: diff.oldMonthlySavings,
    newMonthlySavings: diff.newMonthlySavings,
    oldAnnualSavings: diff.oldAnnualSavings,
    newAnnualSavings: diff.newAnnualSavings,
    deltaMonthlySavings: diff.deltaMonthlySavings,
    deltaAnnualSavings: diff.deltaAnnualSavings,
    deltaDirection,
    changedRecommendations: changedRecs,
    unchangedRecommendations: unchangedRecs,
    changedTools: diff.changedTools,
    stalenessReason: diff.stalenessReason,
    changeSummary: diff.changeSummary,
    staleFlag: diff.staleFlag,
  };
}

// ─── Stale Banner Eligibility ─────────────────────────────────────────────────

/**
 * Determines whether the stale banner should be shown on the result page.
 * Only returns true for material staleness - never for trivial deltas.
 */
export function isStaleBannerEligible(diff: AuditDiff): boolean {
  return diff.staleFlag;
}

// ─── Internal: Classify Recommendations ──────────────────────────────────────

interface ClassifiedRecs {
  changedRecs: Array<{ old: Recommendation | null; new: Recommendation | null }>;
  unchangedRecs: Recommendation[];
}

function classifyRecommendations(
  oldRecs: Recommendation[],
  newRecs: Recommendation[],
): ClassifiedRecs {
  const changedRecs: ClassifiedRecs['changedRecs'] = [];
  const unchangedRecs: Recommendation[] = [];

  const newRecsByToolId = new Map<string, Recommendation>();
  for (const rec of newRecs) {
    newRecsByToolId.set(rec.toolId, rec);
  }

  const oldRecsByToolId = new Map<string, Recommendation>();
  for (const rec of oldRecs) {
    oldRecsByToolId.set(rec.toolId, rec);
  }

  // Check all old recs for changes
  for (const oldRec of oldRecs) {
    const newRec = newRecsByToolId.get(oldRec.toolId);

    if (!newRec) {
      // Was removed
      changedRecs.push({ old: oldRec, new: null });
    } else if (
      newRec.title !== oldRec.title ||
      newRec.type !== oldRec.type ||
      newRec.estimatedSavings !== oldRec.estimatedSavings
    ) {
      // Changed meaningfully
      changedRecs.push({ old: oldRec, new: newRec });
    } else {
      unchangedRecs.push(newRec);
    }
  }

  // Check for new recs that didn't exist before
  for (const newRec of newRecs) {
    if (!oldRecsByToolId.has(newRec.toolId)) {
      changedRecs.push({ old: null, new: newRec });
    }
  }

  return { changedRecs, unchangedRecs };
}

// ─── Re-export for convenience ────────────────────────────────────────────────
export type { AuditDiff, DiffViewModel, StoredAudit, AuditResult };
